import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { formatError } from '../lib/api';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff, ArrowRight, ArrowLeft } from 'lucide-react';
import { Input } from '../components/ui/input';
import SoulBalmLogo from '../components/SoulBalmLogo';

const COUNTRY_CODES = [
  { code: '+91', country: 'IN', flag: '\u{1F1EE}\u{1F1F3}' },
  { code: '+1', country: 'US', flag: '\u{1F1FA}\u{1F1F8}' },
  { code: '+44', country: 'UK', flag: '\u{1F1EC}\u{1F1E7}' },
  { code: '+61', country: 'AU', flag: '\u{1F1E6}\u{1F1FA}' },
  { code: '+971', country: 'AE', flag: '\u{1F1E6}\u{1F1EA}' },
  { code: '+65', country: 'SG', flag: '\u{1F1F8}\u{1F1EC}' },
  { code: '+49', country: 'DE', flag: '\u{1F1E9}\u{1F1EA}' },
  { code: '+33', country: 'FR', flag: '\u{1F1EB}\u{1F1F7}' },
  { code: '+81', country: 'JP', flag: '\u{1F1EF}\u{1F1F5}' },
  { code: '+86', country: 'CN', flag: '\u{1F1E8}\u{1F1F3}' },
  { code: '+82', country: 'KR', flag: '\u{1F1F0}\u{1F1F7}' },
  { code: '+55', country: 'BR', flag: '\u{1F1E7}\u{1F1F7}' },
  { code: '+52', country: 'MX', flag: '\u{1F1F2}\u{1F1FD}' },
  { code: '+39', country: 'IT', flag: '\u{1F1EE}\u{1F1F9}' },
  { code: '+34', country: 'ES', flag: '\u{1F1EA}\u{1F1F8}' },
  { code: '+7', country: 'RU', flag: '\u{1F1F7}\u{1F1FA}' },
  { code: '+27', country: 'ZA', flag: '\u{1F1FF}\u{1F1E6}' },
  { code: '+966', country: 'SA', flag: '\u{1F1F8}\u{1F1E6}' },
  { code: '+62', country: 'ID', flag: '\u{1F1EE}\u{1F1E9}' },
  { code: '+60', country: 'MY', flag: '\u{1F1F2}\u{1F1FE}' },
  { code: '+63', country: 'PH', flag: '\u{1F1F5}\u{1F1ED}' },
  { code: '+64', country: 'NZ', flag: '\u{1F1F3}\u{1F1FF}' },
  { code: '+353', country: 'IE', flag: '\u{1F1EE}\u{1F1EA}' },
  { code: '+47', country: 'NO', flag: '\u{1F1F3}\u{1F1F4}' },
  { code: '+46', country: 'SE', flag: '\u{1F1F8}\u{1F1EA}' },
  { code: '+41', country: 'CH', flag: '\u{1F1E8}\u{1F1ED}' },
  { code: '+31', country: 'NL', flag: '\u{1F1F3}\u{1F1F1}' },
  { code: '+48', country: 'PL', flag: '\u{1F1F5}\u{1F1F1}' },
  { code: '+90', country: 'TR', flag: '\u{1F1F9}\u{1F1F7}' },
  { code: '+234', country: 'NG', flag: '\u{1F1F3}\u{1F1EC}' },
];

export default function SignUpPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [form, setForm] = useState({
    username: '', email: '', country_code: '+91', phone_number: '',
    date_of_birth: '', place_of_birth: '', time_of_birth: '',
    password: '', confirm_password: '', terms: false
  });

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const steps = [
    {
      title: "What shall we call you?",
      subtitle: "This is how the cards will address you.",
      fields: (
        <div className="space-y-4">
          <Input data-testid="signup-username" placeholder="Your name or chosen alias" value={form.username}
            onChange={e => update('username', e.target.value)}
            className="glass-input h-12 rounded-xl px-4 text-[#FFFFF0] placeholder:text-[#BDB2C4]/50 bg-white/5 border-white/10 focus:border-[#D4AF37]" />
          <Input data-testid="signup-email" type="email" placeholder="Email address" value={form.email}
            onChange={e => update('email', e.target.value)}
            className="glass-input h-12 rounded-xl px-4 text-[#FFFFF0] placeholder:text-[#BDB2C4]/50 bg-white/5 border-white/10 focus:border-[#D4AF37]" />
          <div>
            <label className="text-xs text-[#BDB2C4] mb-1 block">Mobile Number</label>
            <div className="flex gap-2">
              <select value={form.country_code} onChange={e => update('country_code', e.target.value)}
                data-testid="signup-country-code"
                className="glass-input h-12 rounded-xl px-3 text-[#FFFFF0] bg-white/5 border-white/10 focus:border-[#D4AF37] w-28 appearance-none text-sm">
                {COUNTRY_CODES.map(c => (
                  <option key={c.code} value={c.code} className="bg-[#0F0B1E]">{c.flag} {c.code}</option>
                ))}
              </select>
              <Input data-testid="signup-phone" type="tel" placeholder="Phone number" value={form.phone_number}
                onChange={e => update('phone_number', e.target.value)}
                className="glass-input h-12 rounded-xl px-4 text-[#FFFFF0] placeholder:text-[#BDB2C4]/50 bg-white/5 border-white/10 focus:border-[#D4AF37] flex-1" />
            </div>
          </div>
        </div>
      ),
      valid: form.username.length >= 2 && form.email.includes('@') && form.phone_number.length >= 6
    },
    {
      title: "Create your sacred key",
      subtitle: "This protects your readings and reflections.",
      fields: (
        <div className="space-y-4">
          <div className="relative">
            <Input data-testid="signup-password" type={showPass ? "text" : "password"} placeholder="Password (min 6 characters)"
              value={form.password} onChange={e => update('password', e.target.value)}
              className="glass-input h-12 rounded-xl px-4 pr-12 text-[#FFFFF0] placeholder:text-[#BDB2C4]/50 bg-white/5 border-white/10 focus:border-[#D4AF37]" />
            <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-3 text-[#BDB2C4]" data-testid="signup-toggle-password">
              {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <Input data-testid="signup-confirm-password" type={showPass ? "text" : "password"} placeholder="Confirm password"
            value={form.confirm_password} onChange={e => update('confirm_password', e.target.value)}
            className="glass-input h-12 rounded-xl px-4 text-[#FFFFF0] placeholder:text-[#BDB2C4]/50 bg-white/5 border-white/10 focus:border-[#D4AF37]" />
          {form.password && form.confirm_password && form.password !== form.confirm_password && (
            <p className="text-xs text-[#C87886]">Passwords do not match</p>
          )}
          <label className="flex items-start gap-3 cursor-pointer" data-testid="signup-terms-label">
            <input type="checkbox" checked={form.terms} onChange={e => update('terms', e.target.checked)}
              className="w-4 h-4 mt-0.5 rounded border-[#D4AF37]/40 bg-white/5 text-[#D4AF37] focus:ring-[#D4AF37]"
              data-testid="signup-terms" />
            <span className="text-xs text-[#BDB2C4]">
              I accept the sacred{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] underline underline-offset-2 hover:brightness-125"
                data-testid="terms-link">Terms and Conditions</a>
              {' '}of this space
            </span>
          </label>
        </div>
      ),
      valid: form.password.length >= 6 && form.password === form.confirm_password && form.terms
    }
  ];

  const handleSubmit = async () => {
    setError('');
    setLoading(true);
    try {
      const phone = form.phone_number ? `${form.country_code}${form.phone_number}` : null;
      await register({
        username: form.username, email: form.email, password: form.password,
        phone: phone, date_of_birth: null,
        place_of_birth: null, time_of_birth: null
      });
      navigate('/home');
    } catch (e) {
      setError(formatError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = () => {
    const redirectUri = window.location.origin + '/auth/callback';
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const googleAuthUrl = `https://accounts.google.com/o/oauth2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=openid%20email%20profile&access_type=offline&prompt=consent`;
    window.location.href = googleAuthUrl;
  };

  const currentStep = steps[step];
  const isLast = step === steps.length - 1;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#070A1A] px-4 py-8" data-testid="signup-page">
      <div className="absolute inset-0 mystic-gradient" />
      <motion.div className="relative z-10 w-full max-w-md" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <div className="text-center mb-8">
          <SoulBalmLogo size={48} className="mx-auto mb-4" />
          <p className="text-xs text-[#D4AF37] tracking-[0.2em] uppercase">Before the cards can speak to you...</p>
          <p className="text-xs text-[#BDB2C4] mt-1">let us know who you are.</p>
        </div>
        <div className="flex gap-2 mb-8 px-4">
          {steps.map((_, i) => (
            <div key={i} className={`flex-1 h-0.5 rounded-full transition-all duration-500 ${i <= step ? 'bg-[#D4AF37]' : 'bg-white/10'}`} />
          ))}
        </div>
        <div className="glass-card p-8">
          {/* Google Sign Up Option */}
          <div className="mb-6">
            <button type="button" onClick={handleGoogleSignUp} data-testid="google-signup-btn"
              className="w-full h-12 rounded-xl flex items-center justify-center gap-3 bg-white/5 border border-white/10 hover:border-[#D4AF37]/40 hover:bg-white/10 transition-all duration-300 text-[#FFFFF0] text-sm">
              <svg width="18" height="18" viewBox="0 0 18 18"><path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z"/><path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z"/><path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z"/><path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58Z"/></svg>
              Sign up with Google
            </button>
            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
              <div className="relative flex justify-center"><span className="bg-[#0F0B1E] px-4 text-xs text-[#BDB2C4]/60">or continue with email</span></div>
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.3 }}>
              <h2 className="font-['Playfair_Display'] text-xl text-[#FFFFF0] mb-2">{currentStep.title}</h2>
              <p className="text-sm text-[#BDB2C4] mb-6">{currentStep.subtitle}</p>
              {currentStep.fields}
            </motion.div>
          </AnimatePresence>
          {error && <p className="text-sm text-[#C87886] mt-4" data-testid="signup-error">{error}</p>}
          <div className="flex justify-between mt-8">
            {step > 0 ? (
              <button onClick={() => setStep(step - 1)} className="flex items-center gap-2 text-sm text-[#BDB2C4] hover:text-[#FFFFF0] transition-colors" data-testid="signup-back-btn">
                <ArrowLeft size={16} /> Back
              </button>
            ) : (
              <Link to="/login" className="text-sm text-[#BDB2C4] hover:text-[#D4AF37] transition-colors">Already have an account?</Link>
            )}
            {isLast ? (
              <button onClick={handleSubmit} disabled={!currentStep.valid || loading}
                className="btn-primary flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed" data-testid="signup-submit-btn">
                {loading ? 'Opening the door...' : 'Enter the Room'} <ArrowRight size={16} />
              </button>
            ) : (
              <button onClick={() => setStep(step + 1)} disabled={!currentStep.valid}
                className="btn-primary flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed" data-testid="signup-next-btn">
                Continue <ArrowRight size={16} />
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
